// v0.4.45 — Amendment-tracking shared hook.
//
// `useAmendmentsCount()` returns the live count of OPEN+SUBMITTED
// rows for the Header nav badge. Polls every 60s so a freshly-opened
// amendment surfaces without a page reload.
//
// `useAmendmentsList({status, overdue})` powers the inbox page with
// the same polling cadence + manual refresh.
import { useCallback, useEffect, useRef, useState } from "react";
import { listAmendments } from "../lib/api";

const POLL_MS = 60_000;

export function useAmendmentsCount() {
  const [count, setCount] = useState(0);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const open = await listAmendments({ status: "OPEN" });
      const sub = await listAmendments({ status: "SUBMITTED" });
      setCount((open?.count || 0) + (sub?.count || 0));
    } catch {
      // Silent — header badge degrades to "no count visible" if
      // the call fails (e.g. operator role lacks ADMIN). 401 is
      // caught by the global axios interceptor.
    }
  }, []);

  useEffect(() => {
    refresh();
    timerRef.current = setInterval(refresh, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [refresh]);

  return { count, refresh };
}

export function useAmendmentsList({ status, overdue }) {
  const [data, setData] = useState({ count: 0, amendments: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listAmendments({ status, overdue });
      setData(res || { count: 0, amendments: [] });
    } catch (e) {
      setError(
        e?.response?.data?.detail?.message
          || e?.response?.data?.detail
          || e?.message
          || "Failed to load amendments",
      );
    } finally {
      setLoading(false);
    }
  }, [status, overdue]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  return { data, loading, error, refresh };
}
